import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, imageUrl } from "../lib/api";
import { CalendarDays, Users, CheckCircle2, Clock } from "lucide-react";
import { DateRangePicker, isoFromDateTime } from "../lib/date-pickers";
import { BookingSuccessDialog } from "../lib/booking-success";
import WhatsAppPush from "../components/WhatsAppPush";
import { PhoneField, PreferredContactMethodField } from "../components/PhoneField";
import { buildPhoneFieldValue, getPhoneFieldError, touchPhoneField } from "../lib/phone";

function toISO(date, time = "10:00") {
  return isoFromDateTime(date, time);
}

export default function Rentals() {
  return (
    <div>
      <PageHeader
        eyebrow="Rentals"
        title="Short-term and long-term van rental."
        subtitle="Check live availability for short stays, or request a tailored long-term offer for 1+ month hires."
      />
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-20 grid gap-12 lg:grid-cols-5">
        <div className="lg:col-span-3 space-y-6">
          <WhatsAppPush />
          <ShortTermBooking />
        </div>
        <aside className="lg:col-span-2 space-y-6">
          <LongTermCTA />
          <Perks />
        </aside>
      </section>
    </div>
  );
}

export function PageHeader({ eyebrow, title, subtitle }) {
  return (
    <section className="border-b border-slate-200 bg-white">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-20">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-sky-700">{eyebrow}</p>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl">
          {title}
        </h1>
        {subtitle && <p className="mt-4 text-slate-600 max-w-2xl">{subtitle}</p>}
      </div>
    </section>
  );
}

function ShortTermBooking() {
  const today = new Date();
  const [range, setRange] = useState({ from: today, to: addDays(today, 3) });
  const [vehicles, setVehicles] = useState(null); // null=not checked
  const [availabilityMeta, setAvailabilityMeta] = useState(null);
  const [checking, setChecking] = useState(false);

  const check = async () => {
    if (!range?.from || !range?.to) {
      toast.error("Pick a start and end date");
      return;
    }
    setChecking(true);
    try {
      const { data } = await api.get("/availability", {
        params: { service: "rental", start: toISO(range.from, "10:00"), end: toISO(range.to, "10:00") },
      });
      setVehicles(data.vehicles || []);
      setAvailabilityMeta(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 sm:p-8" data-testid="short-term-section">
      <h2 className="font-heading text-2xl font-semibold tracking-tight">Short-term rental</h2>
      <p className="mt-1 text-xs font-semibold uppercase tracking-widest text-sky-700">
        Self-drive · 9 seats incl. driver
      </p>
      <p className="mt-2 text-sm text-slate-600">Pick dates and see available vans in real time.</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-[2fr_1fr] items-end">
        <Labeled label="Rental dates">
          <DateRangePicker
            value={range}
            onChange={setRange}
            min={today}
            placeholder="Pick rental dates"
            data-testid="rental-date-range"
          />
        </Labeled>
        <button
          onClick={check}
          disabled={checking}
          data-testid="rental-check-availability"
          className="rounded-md bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {checking ? "Checking…" : "Check availability"}
        </button>
      </div>

      {vehicles !== null && (
        <div className="mt-8" data-testid="rental-availability-result">
          {vehicles.length === 0 ? (
            <EmptyAvailability nextAvailableStart={availabilityMeta?.next_available_start} message={availabilityMeta?.message} />
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                <CheckCircle2 className="inline w-4 h-4 text-emerald-600 mr-1" />
                {vehicles.length} vehicle{vehicles.length > 1 ? "s" : ""} available for your dates.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                {vehicles.map((v) => (
                  <VehicleCard key={v.id} v={v} start={range.from} end={range.to} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function VehicleCard({ v, start, end }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    phone: buildPhoneFieldValue(),
    preferred_contact_method: null,
    passengers: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const phoneError = getPhoneFieldError(form.phone);
    if (phoneError) {
      setForm({ ...form, phone: touchPhoneField(form.phone) });
      toast.error(phoneError);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", {
        service_type: "rental",
        vehicle_id: v.id,
        start_at: toISO(start, "10:00"),
        end_at: toISO(end, "10:00"),
        customer_name: form.customer_name,
        email: form.email,
        phone: form.phone.e164 || null,
        phone_country: form.phone.country || null,
        preferred_contact_method: form.preferred_contact_method || null,
        passengers: form.passengers ? Number(form.passengers) : null,
        notes: form.notes || null,
      });
      setShowForm(false);
      setForm({
        customer_name: "",
        email: "",
        phone: buildPhoneFieldValue(),
        preferred_contact_method: null,
        passengers: "",
        notes: "",
      });
      setCreated(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden bg-white" data-testid={`vehicle-card-${v.id}`}>
      <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
        <img
          src={v.images?.[0] ? imageUrl(v.images[0]) : "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1400&q=75"}
          alt={v.name}
          className="h-full w-full object-cover"
        />
      </div>
      <div className="p-5">
        <h3 className="font-heading text-lg font-semibold">{v.name}</h3>
        <p className="text-xs text-slate-500 mt-1">
          <Users size={12} className="inline mr-1" /> {v.model} · Self-drive · {v.seats} seats incl. driver
        </p>
        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            data-testid={`book-vehicle-${v.id}`}
            className="mt-4 w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Request booking
          </button>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-2">
            <Input required placeholder="Full name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid={`book-name-${v.id}`} />
            <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid={`book-email-${v.id}`} />
            <PhoneField
              value={form.phone}
              onChange={(phone) => setForm({ ...form, phone })}
              countryTestId={`book-phone-country-${v.id}`}
              numberTestId={`book-phone-${v.id}`}
              errorTestId={`book-phone-error-${v.id}`}
            />
            <Labeled label="Preferred contact method">
              <PreferredContactMethodField
                value={form.preferred_contact_method}
                onChange={(preferred_contact_method) => setForm({ ...form, preferred_contact_method })}
                testid={`book-contact-method-${v.id}`}
              />
            </Labeled>
            <Input type="number" min="1" max={v.seats} placeholder={`Passengers (max ${v.seats} incl. driver)`} value={form.passengers} onChange={(e) => setForm({ ...form, passengers: e.target.value })} />
            <textarea rows={2} placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900" />
            <button type="submit" disabled={busy} data-testid={`submit-booking-${v.id}`} className="w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {busy ? "Submitting…" : "Submit request"}
            </button>
          </form>
        )}
      </div>
      {created && <BookingSuccessDialog booking={created} onClose={() => setCreated(null)} />}
    </div>
  );
}

export function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`h-10 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-slate-900 focus:ring-1 focus:ring-slate-900 ${className}`}
    />
  );
}

export function Labeled({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export function EmptyAvailability({ nextAvailableStart, message }) {
  const next = nextAvailableStart ? new Date(nextAvailableStart) : null;
  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center" data-testid="no-availability">
      <Clock className="mx-auto mb-2 text-slate-400" />
      <p className="font-semibold text-slate-900">Fully booked for these dates.</p>
      <p className="text-sm text-slate-600 mt-1">
        {message || "Try different dates or request a quote — we often open extra capacity."}
      </p>
      {next && (
        <p className="mt-2 text-xs font-medium text-slate-500" data-testid="rental-next-available">
          Next available start: {next.toLocaleDateString()} at {next.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </p>
      )}
      <p className="text-sm text-slate-600 mt-3">
        Try different dates or{" "}
        <a href="/contact" className="underline font-semibold">
          request a quote
        </a>{" "}
        — we often open extra capacity.
      </p>
    </div>
  );
}

function LongTermCTA() {
  return (
    <div className="rounded-xl bg-slate-900 text-white p-6">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-300">Long-term</p>
      <h3 className="mt-2 font-heading text-2xl font-semibold">Need a van for 1+ month?</h3>
      <p className="mt-3 text-slate-300 text-sm">
        Long-term hires are handled by request — better rates, flexible terms, and dedicated support for
        companies and agencies.
      </p>
      <a
        href="/long-term"
        data-testid="long-term-cta-link"
        className="mt-5 inline-flex rounded-md bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 hover:bg-sky-50"
      >
        Request long-term offer
      </a>
    </div>
  );
}

function Perks() {
  const items = [
    ["Flexible booking", "Short stays or extended hire."],
    ["No hidden fees", "VAT invoicing available."],
    ["Local support", "Direct WhatsApp contact."],
  ];
  return (
    <div className="rounded-xl border border-slate-200 p-6">
      <h3 className="font-heading text-lg font-semibold">Why IceFleet</h3>
      <ul className="mt-4 space-y-3 text-sm">
        {items.map(([t, d]) => (
          <li key={t}>
            <p className="font-semibold text-slate-900">{t}</p>
            <p className="text-slate-600">{d}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

